import React, { useEffect, useMemo, useRef, useState } from 'react';
import researchService from '../../services/researchService';

/**
 * SecMetricPickerDialog — browse the XBRL tags SEC EDGAR has reported for a
 * company's ticker and pick which ones to add as tracked metrics.
 *
 * Props:
 *   companyId         (string)
 *   existingKeys      (string[])  keys already in the tracked-metrics list
 *                                 (used to disable the Add button for dupes)
 *   onAdd(metric)     called for each selected metric. Shape:
 *                       { key, label, description, xbrl_tag, taxonomy }
 *   onClose()
 *
 * The picker fetches the full tag list once, then filters client-side for
 * snappy search. Taxonomy filter is a dropdown populated from the server.
 */
export default function SecMetricPickerDialog({
  companyId,
  existingKeys = [],
  onAdd,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticker, setTicker] = useState('');
  const [allMetrics, setAllMetrics] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [taxonomyFilter, setTaxonomyFilter] = useState('us-gaap');
  const [search, setSearch] = useState('');
  const [recentOnly, setRecentOnly] = useState(true);
  const [justAdded, setJustAdded] = useState(() => new Set());
  const [expandedRow, setExpandedRow] = useState(null);
  const searchRef = useRef(null);

  // Load full list once
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    researchService
      .listSecAvailableMetrics(companyId, { limit: 5000 })
      .then((data) => {
        if (cancelled) return;
        setTicker(data.ticker || '');
        setAllMetrics(data.metrics || []);
        setTaxonomies(data.taxonomies || []);
        // If the server didn't return us-gaap (e.g. foreign filer), fall back
        // to the first taxonomy that has data.
        if (
          data.taxonomies &&
          data.taxonomies.length > 0 &&
          !data.taxonomies.includes('us-gaap')
        ) {
          setTaxonomyFilter(data.taxonomies[0]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.detail ||
            err.message ||
            'Failed to load SEC metrics',
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Auto-focus the search box once loaded
  useEffect(() => {
    if (!loading && searchRef.current) searchRef.current.focus();
  }, [loading]);

  // Cutoff for the "recent only" toggle — keep tags with at least one fact
  // reported in the last ~2 years.
  const recentCutoff = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return d.toISOString().slice(0, 10);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allMetrics.filter((m) => {
      if (taxonomyFilter && m.taxonomy !== taxonomyFilter) return false;
      if (recentOnly && (!m.latest_period_end || m.latest_period_end < recentCutoff)) return false;
      if (!q) return true;
      const hay = `${m.tag} ${m.label || ''} ${m.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allMetrics, taxonomyFilter, search, recentOnly, recentCutoff]);

  // Cap visible rows for performance — show count if truncated.
  const VISIBLE_CAP = 500;
  const visible = filtered.slice(0, VISIBLE_CAP);
  const truncated = filtered.length > VISIBLE_CAP;

  const existingKeySet = useMemo(
    () => new Set((existingKeys || []).map((k) => k.toLowerCase())),
    [existingKeys],
  );

  // Suggest a snake_case key from an XBRL tag (CamelCase -> snake_case)
  const tagToKey = (tag) =>
    String(tag || '')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .toLowerCase()
      .replace(/^_+|_+$/g, '');

  const handleAdd = (m) => {
    const key = tagToKey(m.tag);
    if (!key) return;
    const metric = {
      key,
      label: m.label || m.tag,
      description: m.description || `SEC XBRL tag ${m.taxonomy}:${m.tag}`,
      xbrl_tag: m.tag,
      taxonomy: m.taxonomy,
    };
    onAdd(metric);
    setJustAdded((prev) => {
      const next = new Set(prev);
      next.add(`${m.taxonomy}:${m.tag}`);
      return next;
    });
  };

  const formatValue = (v, unit) => {
    if (v == null) return '—';
    if (typeof v !== 'number') return String(v);
    const abs = Math.abs(v);
    let s;
    if (abs >= 1e9) s = `${(v / 1e9).toFixed(2)}B`;
    else if (abs >= 1e6) s = `${(v / 1e6).toFixed(2)}M`;
    else if (abs >= 1e3) s = `${(v / 1e3).toFixed(2)}K`;
    else s = String(v);
    return unit ? `${s} ${unit}` : s;
  };

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-search me-2 text-primary" />
              Browse SEC metrics
              {ticker ? (
                <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                  for <strong>{ticker}</strong>
                </span>
              ) : null}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <div className="mt-2 text-muted small">
                  Fetching companyfacts from SEC EDGAR…
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              <>
                <p className="text-muted small mb-3">
                  Click <strong>Add</strong> next to any tag to add it as a tracked
                  custom metric. The metric is bound to the exact XBRL tag, so future
                  SEC backfills will pull it directly without LLM extraction.
                </p>

                <div className="row g-2 mb-3 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label small mb-1">Search</label>
                    <input
                      ref={searchRef}
                      className="form-control form-control-sm"
                      placeholder="tag, label, or description…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Taxonomy</label>
                    <select
                      className="form-select form-select-sm"
                      value={taxonomyFilter}
                      onChange={(e) => setTaxonomyFilter(e.target.value)}
                    >
                      <option value="">All</option>
                      {taxonomies.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sec-picker-recent"
                        checked={recentOnly}
                        onChange={(e) => setRecentOnly(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="sec-picker-recent">
                        Only tags reported in the last 2 years
                      </label>
                    </div>
                  </div>
                </div>

                <div className="small text-muted mb-2">
                  Showing <strong>{visible.length.toLocaleString()}</strong>
                  {truncated ? (
                    <>
                      {' '}of <strong>{filtered.length.toLocaleString()}</strong>{' '}
                      matches (refine your search to see more)
                    </>
                  ) : (
                    <>
                      {' '}of <strong>{allMetrics.length.toLocaleString()}</strong>{' '}
                      total tags
                    </>
                  )}
                </div>

                <div className="table-responsive" style={{ maxHeight: '55vh' }}>
                  <table className="table table-sm table-hover" style={{ fontSize: '0.82rem' }}>
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: '28%' }}>Label</th>
                        <th>Description</th>
                        <th style={{ width: '8%' }} className="text-center">Periods</th>
                        <th style={{ width: '6%' }} className="text-center">Add</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3">
                            No matching tags.
                          </td>
                        </tr>
                      )}
                      {visible.map((m) => {
                        const id = `${m.taxonomy}:${m.tag}`;
                        const key = tagToKey(m.tag);
                        const alreadyTracked = existingKeySet.has(key.toLowerCase());
                        const justAddedThis = justAdded.has(id);
                        const isExpanded = expandedRow === id;
                        return (
                          <React.Fragment key={id}>
                            <tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => setExpandedRow(isExpanded ? null : id)}
                            >
                              <td>
                                <span className="me-1" style={{ fontSize: '0.7rem', color: '#aaa' }}>
                                  {isExpanded ? '▾' : '▸'}
                                </span>
                                {m.label || m.tag}
                              </td>
                              <td
                                className="text-muted"
                                style={{
                                  fontSize: '0.78rem',
                                  maxWidth: 340,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={m.description || ''}
                              >
                                {m.description || ''}
                              </td>
                              <td className="text-center text-muted" style={{ fontSize: '0.78rem' }}>
                                {m.period_count}
                              </td>
                              <td
                                className="text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className={`btn btn-sm py-0 ${
                                    alreadyTracked || justAddedThis
                                      ? 'btn-success'
                                      : 'btn-outline-primary'
                                  }`}
                                  onClick={() => handleAdd(m)}
                                  disabled={alreadyTracked}
                                  title={
                                    alreadyTracked
                                      ? `Already tracked as "${key}"`
                                      : `Add "${key}" as a tracked metric`
                                  }
                                >
                                  {alreadyTracked || justAddedThis ? '✓' : '+'}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr style={{ background: '#f8f9fa' }}>
                                <td colSpan={4} className="py-2 px-3">
                                  <div className="d-flex flex-wrap gap-4" style={{ fontSize: '0.78rem' }}>
                                    <div>
                                      <span className="text-muted me-1">Tag:</span>
                                      <code style={{ fontSize: '0.78rem' }}>{m.tag}</code>
                                    </div>
                                    <div>
                                      <span className="text-muted me-1">Taxonomy:</span>
                                      <span>{m.taxonomy}</span>
                                      {m.is_instant && (
                                        <span className="badge bg-secondary ms-1" style={{ fontSize: '0.65rem' }}>instant</span>
                                      )}
                                    </div>
                                    <div>
                                      <span className="text-muted me-1">Latest period:</span>
                                      <span>{m.latest_period_end || '—'}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted me-1">Latest value:</span>
                                      <span className="font-monospace">
                                        {formatValue(m.latest_value, m.latest_unit)}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
